import { Chip, chipClasses, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { CONVERSATION_COLUMN_MAX_WIDTH } from "./composer-chrome"
import { SEARCH_WIDTH } from "./toolbar-with-search"

const SURFACE_COLUMN_GAP = 1

// The same centred column the transcript and composer use, so the list lines up with the rest of the surface
// instead of running full-bleed. width:100% because auto cross-axis margins turn off the flex stretch.
export const surfaceRootSx = (containerName: string) => ({
  display: "flex", flexDirection: "column", flex: 1, minHeight: 0, p: 1.5, gap: SURFACE_COLUMN_GAP,
  width: "100%", maxWidth: CONVERSATION_COLUMN_MAX_WIDTH, mx: "auto",
  containerType: "inline-size", containerName,
}) satisfies SxProps<Theme>

// mb on top of the column gap: the pills read as their own control strip, not as part of the title.
export const SURFACE_HEADER_SX = { display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", mb: 1 } satisfies SxProps<Theme>

// Centred, because the lifted search shares this row's height (GRID_SX).
export const SURFACE_PILLS_SX = {
  display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", minHeight: "2.5rem",
} satisfies SxProps<Theme>

// MUI palette values as CSS vars, not theme.palette.* — cssVariables freezes those to the light scheme.
export const HOVER_SURFACE = "var(--mui-palette-action-selected)"

/**
 * One style injection for the whole filter row rather than an `sx` per Chip. Selected takes the app's
 * primary straight off the palette (#03a9f4 light, #0277bd dark, where a low-alpha wash all but
 * disappeared) and hovers to the palette's own primary-hover rather than to a grey.
 *
 * Hover is keyed on Chip's own `&.MuiChip-clickable:hover`, which is where MUI declares it — two classes
 * and a pseudo-class, so a plain `&:hover` cannot reach it from `styled` OR from `sx`. Measured both ways.
 */
export const FilterPill = styled(Chip, {
  shouldForwardProp: prop => prop !== "isSelected",
})<{ isSelected: boolean }>(({ isSelected }) => ({
  // The label owns a Chip's horizontal padding — setting it on the root only moves the border.
  height: 28,
  borderRadius: 14,
  [`& .${chipClasses.label}`]: { paddingLeft: 14, paddingRight: 14 },
  ...(isSelected
    ? {
      fontWeight: 500,
      borderColor: "transparent",
      backgroundColor: "var(--app-primary)",
      color: "var(--mui-palette-primary-contrastText)",
      [`&.${chipClasses.clickable}:hover`]: { backgroundColor: "var(--app-opcolor-material-hover)" },
    }
    : {
      [`&.${chipClasses.clickable}:hover`]: { backgroundColor: HOVER_SURFACE },
    }),
}))

export const surfaceGridBaseSx = (theme: Theme, containerName: string) => ({
  flex: 1,
  minHeight: 0,
  border: 0,
  position: "relative",
  // Lifts the toolbar's search onto the pills' row; the overflow pair is what stops the grid clipping it there.
  [`@container ${containerName} (min-width: calc(${SEARCH_WIDTH} * 2))`]: {
    overflow: "visible",
    "& .MuiDataGrid-mainContent": { overflow: "visible" },
    "& .MuiDataGrid-toolbar": {
      position: "absolute",
      bottom: `calc(100% + ${theme.spacing(SURFACE_COLUMN_GAP)})`,
      right: 0,
      left: "auto",
      width: "auto",
      minHeight: 0,
      padding: 0,
      border: 0,
      backgroundColor: "transparent",
    },
  },
}) satisfies SxProps<Theme>
